/**
 * Pilot Location Tracking Service
 * Runs on pilot's mobile device to track GPS location
 * Sends periodic updates to backend
 */

import axios from 'axios';

export class PilotLocationTracker {
  constructor(errandId, token, updateInterval = 10000) {
    this.errandId = errandId;
    this.token = token;
    this.updateInterval = updateInterval; // ms between updates
    this.isTracking = false;
    this.watchId = null;
    this.lastLocation = null;
  }

  /**
   * Start GPS tracking
   */
  async startTracking() {
    if (this.isTracking) return;

    this.isTracking = true;

    // Request location permission
    if (!navigator.geolocation) {
      console.error('Geolocation not supported');
      return false;
    }

    try {
      // Start watching position
      this.watchId = navigator.geolocation.watchPosition(
        (position) => this.handleLocationUpdate(position),
        (error) => this.handleLocationError(error),
        {
          enableHighAccuracy: true, // High accuracy for better tracking
          timeout: 10000,
          maximumAge: 0,
        }
      );

      console.log('✅ GPS Tracking started');
      return true;
    } catch (error) {
      console.error('Failed to start tracking:', error);
      this.isTracking = false;
      return false;
    }
  }

  /**
   * Stop GPS tracking
   */
  stopTracking() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    this.isTracking = false;
    console.log('❌ GPS Tracking stopped');
  }

  /**
   * Handle location update from GPS
   */
  async handleLocationUpdate(position) {
    const { coords, timestamp } = position;

    this.lastLocation = {
      latitude: coords.latitude,
      longitude: coords.longitude,
      accuracy: coords.accuracy,
      speed: coords.speed,
      heading: coords.heading,
      altitude: coords.altitude,
      timestamp,
    };

    // Send to backend
    await this.sendLocationUpdate();
  }

  /**
   * Send location to backend API
   */
  async sendLocationUpdate() {
    if (!this.lastLocation) return;

    try {
      const response = await axios.post(
        '/api/v1/tracking/update',
        {
          errand_id: this.errandId,
          latitude: this.lastLocation.latitude,
          longitude: this.lastLocation.longitude,
          accuracy: this.lastLocation.accuracy,
          speed: this.lastLocation.speed,
          heading: this.lastLocation.heading,
          altitude: this.lastLocation.altitude,
        },
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('📍 Location sent:', {
        lat: this.lastLocation.latitude.toFixed(6),
        lon: this.lastLocation.longitude.toFixed(6),
        speed: this.lastLocation.speed,
      });

      return response.data;
    } catch (error) {
      console.error('Failed to send location:', error.response?.data || error.message);
    }
  }

  /**
   * Handle geolocation errors
   */
  handleLocationError(error) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        console.error('❌ Location permission denied');
        break;
      case error.POSITION_UNAVAILABLE:
        console.error('❌ Location not available');
        break;
      case error.TIMEOUT:
        console.error('❌ Location request timed out');
        break;
      default:
        console.error('❌ Unknown location error');
    }
  }

  /**
   * Get current location instantly (not from watch)
   */
  getCurrentLocation() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          });
        },
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  }

  /**
   * Get last known location
   */
  getLastLocation() {
    return this.lastLocation;
  }

  /**
   * Check if currently tracking
   */
  isCurrentlyTracking() {
    return this.isTracking;
  }
}

// Export singleton instance
export const createPilotTracker = (errandId, token) => {
  return new PilotLocationTracker(errandId, token);
};
